import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Check, ContentCopy } from "@mui/icons-material";
import copy from "copy-to-clipboard";
import { VISUALLY_HIDDEN } from "src/_modules/AiSupportChat/constants";
import { toPlainText } from "src/_modules/AiSupportChat/core/messageText";

/** How long the button shows «تم النسخ» after a copy. */
const COPIED_MS = 2000;

/**
 * Copies an answer as plain text (plan §3.6): what the reader sees, without
 * markdown markers, each link followed by its URL. Shows a check and
 * «تم النسخ» for two seconds, also announced politely.
 *
 * @param {{ content: string }} props the answer's markdown
 */
export default function CopyButton({ content }) {
  const { t } = useTranslation("aiSupport");
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = () => {
    const text = toPlainText(content, { linkUrls: true });
    if (!text || !copy(text, { format: "text/plain" })) return;
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <>
      <Tooltip title={copied ? t("message.copied") : t("message.copy")}>
        <IconButton
          size="small"
          aria-label={t("message.copy")}
          onClick={handleClick}
          sx={{ color: copied ? "success.main" : "text.secondary" }}
        >
          {copied ? (
            <Check fontSize="small" />
          ) : (
            <ContentCopy fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Box role="status" sx={VISUALLY_HIDDEN}>
        {copied ? t("message.copied") : ""}
      </Box>
    </>
  );
}
